/*
 * VAT & IBAN validation — pure & testable. Ported from the legacy inputs
 * (inventory §2). Belgium is the primary market: VAT = BE + 10 digits,
 * IBAN length 16 with mod-97 check. Generic fallbacks cover other EU countries.
 */

// Minimal per-country VAT regexes (extend as needed). Belgium is strict.
const VAT_FORMATS = {
  BE: /^BE\d{10}$/,
  FR: /^FR[A-Z0-9]{2}\d{9}$/,
  NL: /^NL\d{9}B\d{2}$/,
  DE: /^DE\d{9}$/,
  LU: /^LU\d{8}$/,
  IT: /^IT\d{11}$/,
  ES: /^ES[A-Z0-9]\d{7}[A-Z0-9]$/,
}

export function cleanVat(value) {
  return (value || '').toUpperCase().replace(/[\s.-]/g, '')
}

// Belgian VAT/BCE check digits: 10 digits starting 0/1, and the last two equal
// 97 − (first 8 digits mod 97). Format alone lets doomed numbers through (the
// Peppol send would fail on them).
export function isValidBelgianVat(value) {
  const v = cleanVat(value)
  if (!/^BE[01]\d{9}$/.test(v)) return false
  const d = v.slice(2)
  return Number(d.slice(8)) === 97 - (Number(d.slice(0, 8)) % 97)
}

// For a BE number with the right shape but wrong check digits, there is exactly
// ONE valid checksum for the given 8-digit root — suggest it. Returns the
// corrected number, or null when no suggestion applies (bad shape, or already valid).
export function suggestBelgianVat(value) {
  const v = cleanVat(value)
  if (!/^BE[01]\d{9}$/.test(v) || isValidBelgianVat(v)) return null
  const root = v.slice(2, 10)
  const check = 97 - (Number(root) % 97)
  return `BE${root}${String(check).padStart(2, '0')}`
}

export function isValidVat(value) {
  const v = cleanVat(value)
  if (!v) return false
  const cc = v.slice(0, 2)
  if (cc === 'BE') return isValidBelgianVat(v)
  const fmt = VAT_FORMATS[cc]
  if (fmt) return fmt.test(v)
  // Unknown country: accept 2 letters + 2..12 alphanumerics as a loose fallback.
  return /^[A-Z]{2}[A-Z0-9]{2,12}$/.test(v)
}

// IBAN lengths per country (subset; BE = 16).
const IBAN_LENGTHS = {
  BE: 16, FR: 27, NL: 18, DE: 22, LU: 20, IT: 27, ES: 24, PT: 25, GB: 22, CH: 21, AT: 20, IE: 22, FI: 18, DK: 18, SE: 24, NO: 15,
}

export function cleanIban(value) {
  return (value || '').toUpperCase().replace(/\s/g, '')
}

export function formatIban(value) {
  return cleanIban(value).replace(/(.{4})/g, '$1 ').trim()
}

function mod97(iban) {
  // Move first 4 chars to the end, convert letters to numbers, compute mod 97.
  const rearranged = iban.slice(4) + iban.slice(0, 4)
  const numeric = rearranged.replace(/[A-Z]/g, (ch) => String(ch.charCodeAt(0) - 55))
  let remainder = 0
  for (const digit of numeric) remainder = (remainder * 10 + Number(digit)) % 97
  return remainder
}

export function isValidIban(value) {
  const v = cleanIban(value)
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(v)) return false
  const expected = IBAN_LENGTHS[v.slice(0, 2)]
  if (expected && v.length !== expected) return false
  if (v.length < 15 || v.length > 34) return false
  return mod97(v) === 1
}
